'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import type { MicroContentItem } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { cardReset } from '../_shared/styles';
import { KindBadge } from './popup/chrome';

/**
 * Inline-card variant of micro content. Same content kinds the
 * MicroContentPopup handles, but rendered as a small in-context
 * card (no modal chrome). Used by MicroResumeRefresher and
 * MicroPostLessonCheck for "here's a quick check" placements.
 *
 * <p>For non-trivial kinds (multi-question quiz, scenario,
 * interactive flashcards, learning path) the card shows a kicker +
 * "Open in popup →" link so the user gets the full experience. The
 * simple kinds (single-question quiz, weekly read excerpt,
 * reflection) render inline.
 */
export interface MicroPromptCardProps {
  item: MicroContentItem;
  contextLabel?: string;
  onComplete?: (payload?: unknown) => void;
  onDismiss?: () => void;
  /** href template for the "open full" affordance. Default
   *  {@code /micro/{kindSlug}/{id}}. */
  openHrefTemplate?: string;
  compact?: boolean;
}

export function MicroPromptCard({
  item,
  contextLabel,
  onComplete,
  onDismiss,
  openHrefTemplate = '/lxp/micro-learning/{kindSlug}',
  compact = false,
}: MicroPromptCardProps) {
  const { t } = useTranslation('site-builder-blocks');
  const openHref = openHrefTemplate
    .replace('{kindSlug}', encodeURIComponent(item.kindSlug))
    .replace('{id}', encodeURIComponent(String(item.id)));

  return (
    <Card
      style={{
        ...cardReset,
        padding: compact ? 12 : 16,
        background: 'var(--color-surface-card, #fff)',
        borderRadius: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <KindBadge kind={item.kind} />
          {contextLabel && (
            <span style={{ fontSize: '11px', color: 'var(--color-text-tertiary, #94867d)' }}>
              {contextLabel}
            </span>
          )}
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label={t('microPromptCard.skip')}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-text-tertiary, #94867d)',
              fontSize: 'var(--font-size-lg, 18px)',
              cursor: 'pointer',
              padding: 4,
            }}
          >
            ×
          </button>
        )}
      </div>
      <div style={{ fontSize: compact ? 14 : 15, fontWeight: 600, lineHeight: 1.3 }}>
        {item.title}
      </div>
      {item.description && (
        <p
          style={{
            margin: 0,
            fontSize: '13px',
            color: 'var(--color-text-secondary, #71717a)',
            lineHeight: 1.5,
          }}
        >
          {item.description}
        </p>
      )}
      {item.kind === 'QUIZ' && (item.body as { questions?: unknown[] })?.questions?.length === 1 ? (
        <InlineQuiz item={item} onComplete={onComplete} />
      ) : item.kind === 'INTERACTIVE' &&
        (item.body as { interactiveKind?: string })?.interactiveKind === 'reflection' ? (
        <InlineReflection onComplete={onComplete} />
      ) : (
        <Link
          href={openHref}
          style={{
            alignSelf: 'flex-start',
            padding: 'var(--space-1-5, 6px) var(--space-3, 12px)',
            border: '1px solid var(--color-brand-200, #c2d3f7)',
            borderRadius: 6,
            background: 'var(--color-brand-50, #eff6ff)',
            color: 'var(--color-brand-700, #1d4ed8)',
            fontSize: 'var(--font-size-xs, 12px)',
            fontWeight: 600,
            textDecoration: 'none',
          }}
        >
          {t('microPromptCard.openFull')}
        </Link>
      )}
    </Card>
  );
}

function InlineQuiz({
  item,
  onComplete,
}: {
  item: MicroContentItem;
  onComplete?: (payload?: unknown) => void;
}) {
  // Admin canonical quiz body: single-question root fields, or questions[]
  // with {question, options, correctIndex} entries — never {q, choices,
  // correct} (a shape no editor ever wrote).
  const body = item.body as {
    question?: string;
    options?: string[];
    correctIndex?: number;
    questions?: Array<{ question?: string; options?: string[]; correctIndex?: number }>;
  } | null;
  const raw = body?.questions?.[0] ?? body ?? undefined;
  const q =
    raw?.question && Array.isArray(raw.options) && typeof raw.correctIndex === 'number'
      ? { q: raw.question, choices: raw.options, correct: raw.correctIndex }
      : undefined;
  const [picked, setPicked] = useState<number | null>(null);
  const [revealed, setRevealed] = useState(false);
  if (!q) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ fontSize: '13px', fontWeight: 500 }}>{q.q}</div>
      {q.choices.map((c, i) => (
        <button
          key={i}
          type="button"
          disabled={revealed}
          onClick={() => {
            setPicked(i);
            setRevealed(true);
            if (i === q.correct) onComplete?.({ correct: true });
          }}
          style={{
            textAlign: 'left',
            padding: 'var(--space-2, 8px) var(--space-2-5, 10px)',
            border: `1px solid ${
              revealed && i === q.correct
                ? 'var(--color-success-500, #10b981)'
                : revealed && i === picked
                  ? 'var(--color-danger, #dc2626)'
                  : 'var(--color-border, rgba(0,0,0,0.12))'
            }`,
            borderRadius: 6,
            background:
              revealed && i === q.correct
                ? 'var(--color-success-50, #ecfdf5)'
                : 'var(--color-surface-card, #fff)',
            fontSize: '13px',
            cursor: revealed ? 'default' : 'pointer',
          }}
        >
          {c}
        </button>
      ))}
    </div>
  );
}

function InlineReflection({ onComplete }: { onComplete?: (payload?: unknown) => void }) {
  const { t } = useTranslation('site-builder-blocks');
  const [text, setText] = useState('');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        placeholder={t('microPromptCard.reflectionPlaceholder')}
        style={{
          padding: 8,
          border: '1px solid var(--color-border, rgba(0,0,0,0.12))',
          borderRadius: 6,
          fontSize: '13px',
          fontFamily: 'var(--font-family-body, inherit)',
          resize: 'vertical',
        }}
      />
      <button
        type="button"
        onClick={() => text.trim() && onComplete?.({ text })}
        disabled={text.trim().length === 0}
        style={{
          alignSelf: 'flex-start',
          padding: 'var(--space-1-5, 6px) var(--space-3, 12px)',
          border: 'none',
          borderRadius: 6,
          background: 'var(--color-text-primary, #18181b)',
          color: 'var(--color-text-on-brand, #fff)',
          fontSize: 'var(--font-size-xs, 12px)',
          fontWeight: 600,
          cursor: text.trim() ? 'pointer' : 'not-allowed',
          opacity: text.trim() ? 1 : 0.5,
        }}
      >
        {t('microPromptCard.submit')}
      </button>
    </div>
  );
}
